import { useState, useEffect } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api, type JiraIssue } from '../lib/api.js';

const STATUS_ORDER = ['In Progress', 'Scoping', 'To Do', 'Blocked', 'In Review', 'Done'];

function statusStyle(status: string): { bg: string; color: string; border: string } {
  if (status === 'In Progress') return { bg: 'var(--accent-yellow)22', color: 'var(--accent-yellow)', border: 'var(--accent-yellow)44' };
  if (status === 'Done') return { bg: 'var(--accent-green)22', color: 'var(--accent-green)', border: 'var(--accent-green)44' };
  if (status === 'Blocked') return { bg: 'var(--accent-red)22', color: 'var(--accent-red)', border: 'var(--accent-red)44' };
  return { bg: 'var(--accent-blue)22', color: 'var(--accent-blue)', border: 'var(--accent-blue)44' };
}

function priorityColor(p: string | null): string {
  if (p === 'Highest' || p === 'High') return 'var(--accent-red)';
  if (p === 'Medium') return 'var(--accent-orange)';
  return 'var(--accent-green)';
}

function IssueRow({ issue }: { issue: JiraIssue }) {
  const navigate = useNavigate();
  const ss = statusStyle(issue.status);

  return (
    <button
      type="button"
      onClick={() => void navigate({ to: '/tickets/$key', params: { key: issue.key } })}
      className="w-full text-left rounded-xl px-4 py-3 transition-colors duration-100"
      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'var(--accent-blue)')}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="font-mono text-xs shrink-0" style={{ color: 'var(--text-dimmed)', minWidth: '60px' }}>
          {issue.key}
        </span>
        <span className="flex-1 truncate text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
          {issue.summary}
        </span>
        {issue.clientEmr && (
          <span
            className="shrink-0 rounded px-2 py-0.5 text-xs"
            style={{ background: '#06b6d422', color: '#06b6d4', border: '1px solid #06b6d433' }}
          >
            {issue.clientEmr}
          </span>
        )}
        <span className="shrink-0 text-xs w-16 text-right" style={{ color: issue.goLiveDate ? 'var(--accent-yellow)' : 'var(--text-dimmed)' }}>
          {issue.goLiveDate
            ? new Date(issue.goLiveDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
            : '—'}
        </span>
        <span
          className="shrink-0 rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}
        >
          {issue.status}
        </span>
        <span
          className="shrink-0 h-2 w-2 rounded-full"
          title={issue.priority ?? 'None'}
          style={{ background: priorityColor(issue.priority) }}
        />
        {issue.assignee ? (
          <span
            className="shrink-0 flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-bold text-white"
            title={issue.assignee}
            style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
          >
            {issue.assignee[0]?.toUpperCase()}
          </span>
        ) : (
          <span className="shrink-0 h-6 w-6" />
        )}
      </div>
    </button>
  );
}

function StatusGroup({ status, issues }: { status: string; issues: JiraIssue[] }) {
  const [open, setOpen] = useState(true);
  const ss = statusStyle(status);

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 rounded-xl px-4 py-2 text-left"
        style={{ background: ss.bg, border: `1px solid ${ss.border}` }}
      >
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: ss.color }} />
        <span className="text-sm font-semibold flex-1" style={{ color: ss.color }}>{status}</span>
        <span
          className="rounded-full px-2 py-0.5 text-xs font-medium"
          style={{ background: 'rgba(0,0,0,0.25)', color: ss.color }}
        >
          {issues.length}
        </span>
        <span className="text-xs" style={{ color: ss.color }}>{open ? '▾' : '▸'}</span>
      </button>

      {open && (
        <div className="space-y-1 pl-2">
          {issues.map((issue) => (
            <IssueRow key={issue.key} issue={issue} />
          ))}
        </div>
      )}
    </div>
  );
}

export function TicketsPage() {
  usePageTitle('Tickets');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['jira', debouncedSearch],
    queryFn: () => api.listJiraIssues(debouncedSearch || undefined),
    retry: false,
  });

  const issues: JiraIssue[] = data?.issues ?? [];

  const grouped = STATUS_ORDER.map((status) => ({
    status,
    issues: issues.filter((i) => i.status === status),
  })).filter((g) => g.issues.length > 0);

  const knownStatuses = new Set(STATUS_ORDER);
  const other = issues.filter((i) => !knownStatuses.has(i.status));

  const isNotConfigured = error instanceof Error && error.message.toLowerCase().includes('not configured');

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-14 shrink-0 items-center justify-between border-b px-6" style={{ borderColor: 'var(--border)' }}>
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: 'var(--text-secondary)' }}>Jira IM Board</div>
          <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>Tickets</div>
        </div>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search tickets…"
          className="w-64 rounded-xl px-3 py-2 text-sm outline-none"
          style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
        />
      </header>

      <section className="flex-1 overflow-y-auto p-6 space-y-3">
        {isNotConfigured && (
          <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>
            Configure Jira in Settings to load tickets.
          </div>
        )}
        {error && !isNotConfigured && (
          <div className="text-sm" style={{ color: 'var(--accent-red)' }}>
            {error instanceof Error ? error.message : 'Failed to load tickets'}
          </div>
        )}
        {isLoading && (
          <div className="space-y-2">
            {[1, 2, 3].map((n) => (
              <div key={n} className="h-10 rounded-xl animate-pulse" style={{ background: 'var(--bg-tertiary)' }} />
            ))}
          </div>
        )}
        {!isLoading && !error && grouped.map(({ status, issues: g }) => (
          <StatusGroup key={status} status={status} issues={g} />
        ))}
        {!isLoading && !error && other.length > 0 && (
          <StatusGroup status="Other" issues={other} />
        )}
        {!isLoading && !error && issues.length === 0 && !isNotConfigured && (
          <div className="text-sm" style={{ color: 'var(--text-dimmed)' }}>No tickets found.</div>
        )}
      </section>
    </div>
  );
}
